import React, { useState, useEffect } from 'react';

const RSVPInterface = ({ event, userRsvpStatus, onRSVP }) => {
  const [selectedStatus, setSelectedStatus] = useState(userRsvpStatus || null);
  const [guestCount, setGuestCount] = useState(0);
  const [showGuestInput, setShowGuestInput] = useState(false);

  // Reset when user RSVP status changes
  useEffect(() => {
    setSelectedStatus(userRsvpStatus);
    if (userRsvpStatus !== 'going') {
      setShowGuestInput(false);
      setGuestCount(0);
    }
  }, [userRsvpStatus]);

  const handleRSVPClick = (status) => {
    if (status === 'going') {
      setShowGuestInput(true);
      setSelectedStatus(status);
    } else {
      setShowGuestInput(false);
      setGuestCount(0);
      setSelectedStatus(status);
      onRSVP(event, status, 0);
    }
  };

  const handleConfirmRSVP = () => {
    onRSVP(event, 'going', guestCount);
    setShowGuestInput(false);
  };

  const calculateTotalCost = () => {
    if (event.is_paid && event.price) {
      const totalAttendees = 1 + guestCount; // User + guests
      return (parseFloat(event.price) * totalAttendees).toFixed(2);
    }
    return null;
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button 
          className={`flex-1 px-4 py-2 rounded-xl font-medium transition-colors ${
            selectedStatus === 'going'
              ? 'bg-green-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => handleRSVPClick('going')}
        >
          ✓ Yes, I'll attend
        </button>
        <button 
          className={`px-4 py-2 rounded-xl font-medium transition-colors ${
            selectedStatus === 'maybe'
              ? 'bg-yellow-500 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => handleRSVPClick('maybe')}
        >
          ? Maybe
        </button>
        <button 
          className={`px-4 py-2 rounded-xl font-medium transition-colors ${
            selectedStatus === 'not_going'
              ? 'bg-red-500 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          onClick={() => handleRSVPClick('not_going')}
        >
          ✗ No
        </button>
      </div>

      {showGuestInput && (
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <h5 className="font-medium text-green-900 mb-3">Complete your RSVP</h5>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-green-800 mb-1">
                Number of guests (optional)
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setGuestCount(Math.max(0, guestCount - 1))}
                  className="w-8 h-8 rounded-full bg-green-200 text-green-800 hover:bg-green-300 flex items-center justify-center"
                >
                  -
                </button>
                <span className="px-3 py-1 bg-white border border-green-300 rounded text-center min-w-[40px]">
                  {guestCount}
                </span>
                <button
                  onClick={() => setGuestCount(guestCount + 1)}
                  className="w-8 h-8 rounded-full bg-green-200 text-green-800 hover:bg-green-300 flex items-center justify-center"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-green-700 mt-1">
                Total attendees: You + {guestCount} guest{guestCount !== 1 ? 's' : ''} = {1 + guestCount} people
              </p>
            </div>

            {event.is_paid && event.price && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-yellow-600">💰</span>
                  <span className="font-medium text-yellow-900">Payment Required</span>
                </div>
                <p className="text-sm text-yellow-800">
                  ${event.price} per person × {1 + guestCount} attendee{(1 + guestCount) !== 1 ? 's' : ''} = 
                  <span className="font-bold"> ${calculateTotalCost()}</span>
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Payment will be added to your account for processing
                </p>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleConfirmRSVP}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700"
              >
                Confirm RSVP
              </button>
              <button
                onClick={() => {
                  setShowGuestInput(false);
                  setSelectedStatus(null);
                  setGuestCount(0);
                }}
                className="px-4 py-2 text-green-700 border border-green-300 rounded-lg hover:bg-green-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RSVPInterface;